import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {Question, QuestionTypes} from "../types/Question";

type QuestionState = {
    questions: Question[];
    loading: boolean;
    error: string | null;
};

const initialState: QuestionState = {
    questions: [],
    loading: false,
    error: null,
};

const questionSlice = createSlice({
    name: "question",
    initialState,
    reducers: {
        fetchQuestionsRequest: (state, action: PayloadAction<any>) => {
            state.loading = true;
            state.error = null;
        },
        fetchQuestionsSuccess: (state, action: PayloadAction<any[]>) => {
            state.questions = action.payload;
            state.loading = false;
        },
        fetchQuestionsFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        resetQuestions(state) {
            state.questions = [];
        },
        addQuestionRequest: (state, action: PayloadAction<any>) => {
            state.loading = true;
        },
        addQuestionSuccess: (state, action: PayloadAction<Question>) => {
            state.questions.push(action.payload);
            state.loading = false;
        },
        addQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        addChildQuestionRequest: (state, action: PayloadAction<any>) => {
            state.loading = true;
        },
        addChildQuestionSuccess: (state, action: PayloadAction<Question>) => {
            state.questions = state.questions.map((q) => {
                if (q.id == action.payload.parentId) {
                    const updatedChilds = q.childs?.some(child => child.uid == action.payload.uid)
                        ? q.childs.map(child => child.uid == action.payload.uid ? action.payload : child)
                        : [...(q.childs || []), action.payload];

                    return {
                        ...q,
                        childs: updatedChilds,
                    };
                }
                return q;
            });
            state.loading = false;
        },
        addChildQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        updateQuestionRequest: (state, action: PayloadAction<{
            id: string;
            examId: string;
            data: Partial<Question>
        }>) => {
            state.loading = true;
        },
        updateQuestionSuccess: (state, action: PayloadAction<Question>) => {
            state.questions = state.questions.map((q) =>
                q.id == action.payload.id ? action.payload : q
            );
            state.loading = false;
        },
        updateQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        updateChildQuestionRequest: (state, action: PayloadAction<{
            id: string;
            examId: string;
            data: Partial<Question>
        }>) => {
            state.loading = true;
        },
        updateChildQuestionSuccess: (state, action: PayloadAction<Question>) => {
            state.questions = state.questions.map((q) => {
                if (q.id == action.payload.parentId) {
                    const updatedChilds = q.childs?.some(child => child.id == action.payload.id)
                        ? q.childs.map(child => child.id == action.payload.id ? action.payload : child)
                        : [...(q.childs || []), action.payload];

                    return {
                        ...q,
                        childs: updatedChilds,
                    };
                }
                return q;
            });
            state.loading = false;
        },
        updateChildQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        deleteQuestionRequest: (state, action: PayloadAction<{ id: string; examId: string }>) => {
            state.loading = true;
        },
        deleteQuestionSuccess: (state, action: PayloadAction<{ id: string; examId: string }>) => {
            state.questions = state.questions.filter((q) => q.id != action.payload.id);
            state.loading = false;
        },
        deleteQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        deleteChildQuestionRequest: (state, action: PayloadAction<{ id: string; uid: string; examId: string }>) => {
            state.loading = true;
        },
        deleteChildQuestionSuccess: (state, action: PayloadAction<{ id: string; uid: string; examId: string }>) => {
            state.questions = state.questions.map((q) => {
                if (q.childs) {
                    return {
                        ...q,
                        childs: q.childs.filter((child) => child.uid != action.payload.uid),
                    };
                }
                return q;
            });
            state.loading = false;
        },
        deleteChildQuestionFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        updateAnswerRequest: (state, action: PayloadAction<{
            examId: string;
            questionId: string;
            answerId: string;
            title: string;
            type?: QuestionTypes;
            isCorrect: number;
        }>) => {
            state.loading = true;
        },
        updateAnswerSuccess: (state, action: PayloadAction<{ questionId: string; answer: any }>) => {
            state.questions = state.questions.map((q: any) =>
                q.id == action.payload.questionId
                    ? {
                        ...q,
                        answers: action.payload.answer.answers,
                    }
                    : q
            );
            state.loading = false;
        },
        updateAnswerFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        updateChildAnswerRequest: (state, action: PayloadAction<{
            examId: string;
            questionId: string;
            answerId: string;
            title: string;
            type?: QuestionTypes;
            isCorrect: number;
        }>) => {
            state.loading = true;
        },
        updateChildAnswerSuccess: (state, action: PayloadAction<{ questionId: string; answer: any }>) => {
            state.questions = state.questions.map((q: any) => ({
                ...q,
                childs: q.childs
                    ? q.childs.map((child: any) => {
                            return String(child.id) === String(action.payload.questionId)
                                ? {
                                    ...child,
                                    answers: action.payload.answer.answers,
                                }
                                : child
                        }
                    )
                    : q.childs,
            }));
            state.loading = false;
        },
        updateChildAnswerFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        addAnswerRequest: (state, action: PayloadAction<{
            examId: string;
            uid?: string;
            questionId: string;
            title: string;
            type?: QuestionTypes;
            isCorrect: number;
        }>) => {
            state.loading = true;
        },
        addAnswerSuccess: (state, action: PayloadAction<{ questionId: string; answer: any }>) => {
            state.questions = state.questions.map((q: any) => {
                console.log("q.answers", q.answers)
                // Cập nhật câu hỏi cha
                if (q.id == action.payload.questionId) {
                    return {
                        ...q,
                        answers: q.answers
                            ? (() => {
                                const index = q.answers.findIndex(
                                    (a: any) => a.uid == action.payload.answer.uid
                                );
                                if (index !== -1) {
                                    // Cập nhật phần tử đã tồn tại
                                    const updatedAnswers = [...q.answers];
                                    updatedAnswers[index] = action.payload.answer;
                                    return updatedAnswers;
                                } else {
                                    // Thêm mới nếu chưa tồn tại
                                    return [...q.answers, action.payload.answer];
                                }
                            })()
                            : [action.payload.answer],
                    };
                }

                // Cập nhật nếu là câu hỏi con
                if (q.childs) {
                    const updatedChilds = q.childs.map((child: any) => {
                        if (child.id == action.payload.questionId) {
                            return {
                                ...child,
                                answers: child.answers
                                    ? (() => {
                                        const index = child.answers.findIndex(
                                            (a: any) => a.uid == action.payload.answer.uid
                                        );
                                        if (index !== -1) {
                                            const updatedAnswers = [...child.answers];
                                            updatedAnswers[index] = action.payload.answer;
                                            return updatedAnswers;
                                        } else {
                                            return [...child.answers, action.payload.answer];
                                        }
                                    })()
                                    : [action.payload.answer],
                            };
                        }
                        return child;
                    });

                    return {
                        ...q,
                        childs: updatedChilds,
                    };
                }

                // Trường hợp không thay đổi
                return q;
            });
            state.loading = false;
        },
        addAnswerFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
        deleteAnswerRequest: (state, action: PayloadAction<{ examId: string; questionId: string; answer: any }>) => {
            state.loading = true;
        },
        deleteAnswerSuccess: (state, action: PayloadAction<{ questionId: string; answer: any }>) => {
            console.log("deleteAnswerSuccess", action.payload)
            state.questions = state.questions.map((q: any) => {
                if (q.id == action.payload.questionId) {
                    return {
                        ...q,
                        answers: q.answers.filter((opt: any) => opt.id !== action.payload.answer.id),
                    };
                }

                if (q.childs) {
                    const updatedChilds = q.childs.map((child: any) => {
                        if (child.id == action.payload.questionId) {
                            return {
                                ...child,
                                answers: child.answers.filter((opt: any) => opt.id !== action.payload.answer.id),
                            };
                        }
                        return child;
                    });

                    return {
                        ...q,
                        childs: updatedChilds,
                    };
                }

                return q;
            });
            state.loading = false;
        },
        deleteAnswerFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.loading = false;
        },
    },
});

export const {
    fetchQuestionsRequest,
    fetchQuestionsSuccess,
    fetchQuestionsFailure,
    resetQuestions,
    addQuestionRequest,
    addQuestionSuccess,
    addQuestionFailure,
    addChildQuestionRequest,
    addChildQuestionSuccess,
    addChildQuestionFailure,
    updateChildQuestionRequest,
    updateChildQuestionSuccess,
    updateChildQuestionFailure,
    updateQuestionRequest,
    updateQuestionSuccess,
    updateQuestionFailure,
    deleteQuestionRequest,
    deleteQuestionSuccess,
    deleteQuestionFailure,
    deleteChildQuestionRequest,
    deleteChildQuestionSuccess,
    deleteChildQuestionFailure,
    updateAnswerRequest,
    updateAnswerSuccess,
    updateAnswerFailure,
    updateChildAnswerRequest,
    updateChildAnswerSuccess,
    updateChildAnswerFailure,
    addAnswerRequest,
    addAnswerSuccess,
    addAnswerFailure,
    deleteAnswerRequest,
    deleteAnswerSuccess,
    deleteAnswerFailure,
} = questionSlice.actions;

export default questionSlice.reducer;
